import { Box, TextField, Tooltip, Typography } from '@mui/material';
import * as React from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { DateFormatsEnum } from '../../../constants/date';
import { ON_HOLD_ERROR_TOOLTIP_TEXT } from '../../../constants/oneLink';
import { BUSINESS_REASON_ID } from '../../../constants/reasons';
import { IOneLinkErrors, OneLinkChunksEnum } from '../../../contexts/OneLinkSummaryProvider';
import { selectBusinessReasons } from '../../../store/selectors/reasons';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { formatDate, isValidDate } from '../../../utils/date';
import { isDigitalPYGReport, isDigitalReport } from '../../../utils/reports';
import { ErrorLine } from '../../_mui/_components_';
import { Label, Section } from './common';

interface IProps {
  onEdit?: () => void;
  instance: IOneLinkWithCampaignSummaries | null | undefined;
  errors?: IOneLinkErrors;
}

export const AdditionalSection: React.FC<IProps> = ({ onEdit, errors, instance }) => {
  const reasons = useSelector(selectBusinessReasons);

  const isDigitalOL = useMemo(
    () =>
      isDigitalReport(instance as IOneLinkWithCampaignSummaries) ||
      isDigitalPYGReport(instance as IOneLinkWithCampaignSummaries),
    [instance],
  );

  const onHoldUntilError = useMemo(() => errors?.[OneLinkChunksEnum.AdditionalInfo]?.on_hold_until, [errors]);
  const businessReasonError = useMemo(() => errors?.[OneLinkChunksEnum.AdditionalInfo]?.business_reason_id, [errors]);

  const businessReason = React.useMemo(() => {
    return reasons?.find((reason) => reason.uid === instance?.[BUSINESS_REASON_ID]);
  }, [reasons, instance?.[BUSINESS_REASON_ID]]);

  const onHoldUntil = useMemo(() => {
    if (instance?.on_hold_until && isValidDate(new Date(instance?.on_hold_until))) {
      return formatDate(instance?.on_hold_until, DateFormatsEnum.ddMMMyyyy);
    }
    return 'Date not set';
  }, [instance?.on_hold_until]);

  return (
    <Section title="Additional" onEdit={onEdit} minHeight={420}>
      <Box sx={{ height: 300 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: '94px', position: 'relative' }}>
            {onHoldUntilError && <ErrorLine />}
            {onHoldUntilError ? (
              <Tooltip title={ON_HOLD_ERROR_TOOLTIP_TEXT} arrow placement="top-start">
                <span>
                  {/* Required to ensure Tooltip works on non-button elements */} <Label>On Hold Until</Label>
                </span>
              </Tooltip>
            ) : (
              <Label>On Hold Until</Label>
            )}
            <Typography>{onHoldUntil}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 6 }}>
            <Label>Sender Notifications</Label>
            <Typography>{instance?.disable_sender_email_notifications ? 'Disabled' : 'Enabled'}</Typography>
          </Box>
          {!isDigitalOL && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1, gap: 6 }}>
              <Label>Tracking Notification</Label>
              <Typography>{instance?.disable_recipient_shipped_email ? 'Disabled' : 'Enabled'}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ position: 'relative' }}>
          {businessReasonError && <ErrorLine />}
          <TextField
            label="Business Reason"
            variant="outlined"
            value={businessReason?.name ?? ''}
            aria-readonly={true}
            fullWidth
            placeholder="Select Business Reason"
            disabled
          />
        </Box>
        <TextField
          placeholder="Put down your internal note"
          multiline
          minRows={5}
          maxRows={5}
          label="Internal Notes"
          variant="outlined"
          value={instance?.internal_notes ?? ''}
          fullWidth
          disabled
          sx={{ mt: 2 }}
        />
      </Box>
    </Section>
  );
};
